// src/components/RequireAuth.tsx
//
// The operator portal's gate.
//
// This used to call signinRedirect() from react-oidc-context, which is what
// sent every visit to auth.citstime.app — before /signin could ever render, so
// the Better Auth sign-in page built for this portal was unreachable code.
// It now gates on the Better Auth session and sends people to our own page.
//
// TWO DISTINCT REFUSALS, deliberately not merged:
//
//   no session          -> /signin           (prove who you are)
//   session, no 2FA     -> /security/setup   (finish enrolling)
//
// The second is why the portal does NOT simply refuse a password-only sign-in.
// Enrolling a second factor requires being signed in, so refusing to sign in
// without one is a deadlock — which is exactly the state this portal was in:
// OperatorSignIn rejected any account with no 2FA, and no screen existed to
// enrol. A password-only operator is let in, and led straight to enrolment.

import { type ReactNode } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import { useOperatorAuth } from '../context/OperatorAuthProvider'

type Props = { children: ReactNode }

export default function RequireAuth({ children }: Props) {
  const { isAuthenticated, isLoading, twoFactorEnabled } = useOperatorAuth()
  const location = useLocation()

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-slate-600">Loading…</div>
      </div>
    )
  }

  if (!isAuthenticated) {
    // `state.from` lets the sign-in page return the operator to where they were
    // aiming instead of dumping everyone on the dashboard.
    return <Navigate to="/signin" replace state={{ from: location.pathname }} />
  }

  // Enrolment itself must stay reachable, or this is a redirect loop.
  if (!twoFactorEnabled && !location.pathname.startsWith('/security')) {
    return <Navigate to="/security/setup" replace state={{ from: location.pathname }} />
  }

  return <>{children}</>
}
